import React, {useState} from 'react';
import {v4 as uuid} from 'uuid';
import DeleteProjectModal from '@contentlab/components/delete-project-modal';
import RenameProjectModal from '@contentlab/components/rename-project-modal';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import useDeleteProject from '@contentlab/hooks/mutations/use-delete-project';
import useUpdateProject from '@contentlab/hooks/mutations/use-update-project';
import ProjectAction from '@contentlab/types/ProjectAction';
import {ProjectProps} from '@contentlab/types/projectProps';
import downloadProject from '@contentlab/utils/downloadProject';

/**
 * Parameters of the useProjectActions hook.
 */
export interface UseProjectActionsParams {
  interaction_location: string;
  flow_name?: string;
  onActionComplete?: (action: ProjectAction) => void;
}

/**
 * Return type of the useProjectActions hook.
 */
export interface UseProjectActionsReturn {
  onProjectActionClick: (action: ProjectAction, project: ProjectProps) => void;
  projectActionModals: React.ReactNode;
}

/**
 * Hook for handling project actions.
 *
 * @param interaction_location - The location of the interaction.
 * @param flow_name - The name of the flow.
 * @param onActionComplete - The function to call when the action is complete.
 *
 * @returns An object with the following properties:
 * - onProjectActionClick: A function to handle project actions.
 * - projectActionModals: A React node containing the project action modals.
 */
function useProjectActions({
  interaction_location,
  flow_name,
  onActionComplete,
}: UseProjectActionsParams): UseProjectActionsReturn {
  const {uiInteraction} = useUiInteractionEvent();
  const {started, ended} = useProcessEvent();

  const deleteProjectMutation = useDeleteProject();
  const [deleteModal, setDeleteModal] = useState<{
    projectId: string;
    projectName: string;
  } | null>(null);

  const updateProjectMutation = useUpdateProject();
  const [renameModal, setRenameModal] = useState<{
    projectId: string;
    projectName: string;
  } | null>(null);

  const onRenameClick = async (project: ProjectProps) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'rename_project_modal_open',
      interaction_location,
      flow_name,
      asset_id: project.id,
      interaction_id: interactionId,
    });

    setRenameModal({
      projectId: project.id,
      projectName: project.name,
    });
  };

  const onDownloadClick = async (project: ProjectProps) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'download_project',
      interaction_location,
      flow_name,
      asset_id: project.id,
      interaction_id: interactionId,
    });
    await downloadProject(project.id, interactionId, interaction_location, {
      started,
      ended,
    });

    onActionComplete?.(ProjectAction.Download);
  };

  const onDeleteClick = async (project: ProjectProps) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'delete_project_modal_open',
      interaction_location,
      flow_name,
      asset_id: project.id,
      interaction_id: interactionId,
    });

    setDeleteModal({
      projectId: project.id,
      projectName: project.name,
    });
  };

  const onProjectActionClick = (
    action: ProjectAction,
    project: ProjectProps
  ) => {
    switch (action) {
      case ProjectAction.Rename:
        onRenameClick(project);
        break;
      case ProjectAction.Download:
        onDownloadClick(project);
        break;
      case ProjectAction.Delete:
        onDeleteClick(project);
        break;
      default:
        throw new Error(`Unknown project action: ${action}`);
    }
  };

  // Modal handlers
  const handleRenameProject = async (newName: string) => {
    if (!renameModal) return;

    const interactionId = uuid();
    uiInteraction({
      item_name: 'rename_project',
      interaction_location,
      flow_name,
      asset_id: renameModal.projectId,
      interaction_id: interactionId,
    });

    await updateProjectMutation.mutateAsync({
      projectId: renameModal.projectId,
      params: {
        projectName: newName,
      },
    });

    onActionComplete?.(ProjectAction.Rename);
    setRenameModal(null);
  };

  const handleDeleteProject = async () => {
    if (!deleteModal) return;

    const interactionId = uuid();
    uiInteraction({
      item_name: 'delete_project',
      interaction_location,
      flow_name,
      asset_id: deleteModal.projectId,
      interaction_id: interactionId,
    });

    await deleteProjectMutation.mutateAsync(deleteModal.projectId);

    onActionComplete?.(ProjectAction.Delete);
    setDeleteModal(null);
  };

  const handleCloseRenameModal = () => {
    if (renameModal) {
      const interactionId = uuid();
      uiInteraction({
        item_name: 'rename_project_modal_cancel',
        interaction_location,
        flow_name,
        asset_id: renameModal.projectId,
        interaction_id: interactionId,
      });
    }
    setRenameModal(null);
  };

  const handleCloseDeleteModal = () => {
    if (deleteModal) {
      const interactionId = uuid();
      uiInteraction({
        item_name: 'delete_project_modal_cancel',
        interaction_location,
        flow_name,
        asset_id: deleteModal.projectId,
        interaction_id: interactionId,
      });
    }
    setDeleteModal(null);
  };

  return {
    onProjectActionClick,
    projectActionModals: (
      <>
        <RenameProjectModal
          open={!!renameModal}
          onClose={handleCloseRenameModal}
          onRename={handleRenameProject}
          currentProjectName={renameModal?.projectName || ''}
          isLoading={updateProjectMutation.isLoading}
        />

        <DeleteProjectModal
          open={!!deleteModal}
          onClose={handleCloseDeleteModal}
          onDelete={handleDeleteProject}
          projectName={deleteModal?.projectName || ''}
          isLoading={deleteProjectMutation.isLoading}
        />
      </>
    ),
  };
}

export default useProjectActions;
